import {View, Text, TextInput, ScrollView, SafeAreaView, TouchableOpacity} from 'react-native';
import React, {useEffect, useState, useMemo} from 'react';
import NavigationBar from 'NavigationBar';
import {_m} from 'INTL';
import SKZPersonalInfo from 'SKZPersonalInfo';
import {emailInputStyle, inputStyleFocused, styles} from './UserInformationStyles';
import SkillzImage from 'SkillzImage';
import PushButton from 'PushButton';
import SkillzSVG from 'SkillzSVG';
import {default as SkillzStyle} from 'SkillzStyle';
import AWSManager from 'AWSManager';
import {CHECKOUT_USER_DETAIL_CONTINUE_CLICK} from 'AWSConstants';
import CurrentUserStore from 'CurrentUserStore';
import {updateUserPersonalInfoMutation} from '@api/User';
import SecureBankingDrawer from './SecureBankingDrawer';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';

const {tabHighlightColor} = SkillzStyle.fullStyles;

type UserInfoFormData = {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
};

export type UserInfoProps = {
  onBackNavigation: () => void;
  onUpdateUserInfoError: (errorMessage: string) => void;
  currentUserInfo: SKZPersonalInfo;
  handleComplete: (formData: UserInfoFormData) => void;
};

const UserInformationScreen = ({
  onBackNavigation,
  onUpdateUserInfoError,
  currentUserInfo,
  handleComplete,
}: UserInfoProps) => {
  const [isSecurityPopupVisible, setIsSecurityPopupVisible] = useState(false);

  const securityPopUp = () => setIsSecurityPopupVisible(true);
  const handleSecurityDismiss = () => setIsSecurityPopupVisible(false);

  const {first_name, last_name, phone_number, email} = currentUserInfo;
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [emailAddress, setEmailAddress] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [isSubmittingForm, setIsSubmittingForm] = useState<boolean>(false);
  const [inputFocused, setInputFocused] = useState<string>('given-name');

  const [canEditName, setCanEditName] = useState<boolean>(true);

  useEffect(() => {
    trackTelemetryEvent({
      event: TelemetryEventName.STORE_BANK_LINKING_ACCOUNT_INFO_PAGE_SHOWN,
      includeUserWalletDetails: true,
    });
    CurrentUserStore.getUserProperty('name.confirmed').then((isNameConfirmed: string) => {
      setCanEditName(isNameConfirmed !== 'true');
    });
  }, []);

  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  const isFormValid = useMemo(() => {
    return firstName && lastName && phoneNumber && emailAddress && emailRegex.test(emailAddress);
  }, [firstName, lastName, phoneNumber, emailAddress]);

  const isButtonEnabled = Boolean(!emailError && isFormValid && !isSubmittingForm);

  useEffect(() => {
    setFirstName(first_name);
    setLastName(last_name);
    setPhoneNumber(phone_number);
    setEmailAddress(email);
  }, []);

  // form validations
  const validateEmailFormat = (text: string) => {
    setEmailAddress(text);
    if (text === '' || !text) {
      setEmailError(_m('Email is required'));
    } else if (emailRegex.test(text) === false) {
      setEmailError(_m('Invalid email format'));
    } else {
      setEmailError('');
    }
  };

  // form submission to continue
  const handleFormSubmit = async () => {
    trackTelemetryEvent({
      event: TelemetryEventName.STORE_BANK_LINKING_ACCOUNT_INFO_PAGE_SUBMIT_CLICK,
      includeUserWalletDetails: true,
    });
    // double checking form state before submission
    if (isFormValid) {
      setIsSubmittingForm(true);
      const formData = {
        first_name: firstName,
        last_name: lastName,
        email: emailAddress,
        phone_number: phoneNumber,
      };

      if (!currentUserInfo.email) {
        await updateUserEmail(); // throws on any server error
      }

      handleComplete(formData);

      CurrentUserStore.getCurrentUserId().then((userId: string) => {
        // send AWS Event when user confirms account information
        const awsUserConfirmationData = JSON.stringify({
          USER_ID: userId,
        });
        AWSManager.trackEvent(CHECKOUT_USER_DETAIL_CONTINUE_CLICK, {
          result: awsUserConfirmationData,
        });
      });
    }
  };

  // update user email if it is not set
  const updateUserEmail = async () => {
    return updateUserPersonalInfoMutation.mutate({
      variables: {
        data: {
          email: emailAddress,
        },
      },
      onError: (error) => {
        setIsSubmittingForm(false);
        console.error('Error updating user personal info:', error);

        if (error.name === 'EMAIL_ALREADY_EXISTS') {
          onUpdateUserInfoError(error.message as string);
          return;
        }

        // Handle other errors
        onUpdateUserInfoError(_m('Failed to update user information. Please try again later.'));
      },
    });
  };

  // opacity change for when button is disabled
  const disabledStyle = isButtonEnabled ? {} : {opacity: 0.5};

  // input styles

  return (
    <SafeAreaView style={styles.mainContainer}>
      <NavigationBar
        hasBackButton={true}
        onBackButtonPress={onBackNavigation}
        title={_m('DEPOSIT')}
        hasRightComponent
        renderRightComponent={
          <TouchableOpacity onPress={securityPopUp} hitSlop={10}>
            <SkillzImage
              style={styles.shieldIcon}
              source='icon_shield_white.svg'
              tintColor='#FFFFFF'
            />
          </TouchableOpacity>
        }
      />
      <ScrollView style={{backgroundColor: '#FAFAFA'}}>
        <View style={styles.screenContentContainer}>
          <View style={styles.userInfoContainer}>
            <View>
              <SkillzImage source='account_info_icon.png' style={styles.accountInfoIcon} />
              <Text style={styles.formTitle}>{_m('Account Information')}</Text>
              <Text style={styles.subTitle}>
                {_m('Please enter your information to match\nyour banking records.')}
              </Text>
            </View>
            <View>
              <View style={styles.lockableInputContainer}>
                <TextInput
                  style={
                    canEditName
                      ? inputFocused === 'given-name'
                        ? inputStyleFocused
                        : styles.inputStyle
                      : styles.inputStyleDisabled
                  }
                  onChangeText={(text: string) => setFirstName(text)}
                  value={firstName}
                  accessibilityLabel='legal first name'
                  keyboardType='default'
                  placeholder='Legal First Name'
                  placeholderTextColor={'#7A7A7A'}
                  autoComplete='given-name'
                  onFocus={() => setInputFocused('given-name')}
                  editable={canEditName}
                />
                {!canEditName && (
                  <SkillzSVG
                    source={'ic_lock.svg'}
                    style={styles.lockIcon}
                    tintColor='rgb(172,172,172)'
                  />
                )}
              </View>
              <View style={styles.lockableInputContainer}>
                <TextInput
                  style={
                    canEditName
                      ? inputFocused === 'family-name'
                        ? inputStyleFocused
                        : styles.inputStyle
                      : styles.inputStyleDisabled
                  }
                  onChangeText={(text: string) => setLastName(text)}
                  value={lastName}
                  accessibilityLabel='legal last name'
                  keyboardType='default'
                  placeholder='Legal Last Name'
                  placeholderTextColor={'#7A7A7A'}
                  autoComplete='family-name'
                  onFocus={() => setInputFocused('family-name')}
                  editable={canEditName}
                />
                {!canEditName && (
                  <SkillzSVG
                    source={'ic_lock.svg'}
                    style={styles.lockIcon}
                    tintColor='rgb(172,172,172)'
                  />
                )}
              </View>
              <View style={styles.inputContainer}>
                <TextInput
                  style={emailInputStyle(inputFocused, Boolean(emailError))}
                  onChangeText={(text: string) => validateEmailFormat(text)}
                  value={emailAddress}
                  textContentType='emailAddress'
                  keyboardType='email-address'
                  returnKeyType='done'
                  accessibilityLabel='email address'
                  placeholder='Email (For Validation)'
                  placeholderTextColor={'#7A7A7A'}
                  autoComplete='email'
                  onFocus={() => setInputFocused('email')}
                  autoCapitalize='none'
                />
                {<Text style={styles.inputError}>{emailError ? emailError : ' '}</Text>}
              </View>
              <View style={styles.lockableInputContainer}>
                <TextInput
                  style={styles.inputStyleDisabled}
                  onChangeText={setPhoneNumber}
                  value={phoneNumber}
                  keyboardType='numeric'
                  editable={false}
                  accessibilityLabel='phone number'
                />
                {!canEditName && (
                  <SkillzSVG
                    source={'ic_lock.svg'}
                    style={styles.lockIcon}
                    tintColor='rgb(172,172,172)'
                  />
                )}
              </View>
            </View>
            <View style={styles.aeropayIcon}>
              <SkillzSVG style={styles.aeropayIcon} source='secured_by_aeropay.svg' />
            </View>
          </View>
        </View>
      </ScrollView>
      <View style={styles.buttonContainer}>
        <PushButton
          cornerRadius={50}
          colors={[tabHighlightColor.color]}
          onPress={handleFormSubmit}
          style={[styles.submitButton, disabledStyle]}
          textStyle={styles.submitText}
          title={_m('CONFIRM')}
          disabled={!isButtonEnabled}
          accessibilityLabel={'Confirm Account Information'}
        />
      </View>
      <SecureBankingDrawer visible={isSecurityPopupVisible} onDismiss={handleSecurityDismiss} />
    </SafeAreaView>
  );
};

export default UserInformationScreen;
